import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CkbLogo from '../components/CkbLogo';
import './login.css';

const PROMPT = 'ckb@panel:~$ ';

const BOOT_LINES = [
  { type: 'out', text: 'CKB' },
  { type: 'out', text: '────────────────────────────────────────' },
  { type: 'out', text: '' },
  { type: 'out', text: 'type credentials at the prompt. press enter to continue.' },
];

function HistoryLine({ line }) {
  if (line.type === 'cmd') {
    return (
      <div className="login-terminal-line">
        <span className="prompt">{PROMPT}</span>
        <span className="cmd">{line.text}</span>
      </div>
    );
  }
  return (
    <div className={`login-terminal-line login-line-${line.type}`}>
      <span className={line.type === 'err' ? 'err' : 'out'}>{line.text || '\u00A0'}</span>
    </div>
  );
}

export default function Login() {
  const [history, setHistory] = useState([]);
  const [step, setStep] = useState('boot');
  const [input, setInput] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [bootIndex, setBootIndex] = useState(0);
  const inputRef = useRef(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const focusInput = useCallback(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    focusInput();
  }, [step, loading, focusInput]);

  useEffect(() => {
    if (step !== 'boot') return undefined;
    if (bootIndex >= BOOT_LINES.length) {
      const t = setTimeout(() => setStep('username'), 400);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => {
      setHistory((h) => [...h, BOOT_LINES[bootIndex]]);
      setBootIndex((i) => i + 1);
    }, bootIndex === 0 ? 200 : 120);
    return () => clearTimeout(t);
  }, [step, bootIndex]);

  const appendHistory = (line) => {
    setHistory((h) => [...h, line]);
  };

  const resetSession = () => {
    appendHistory({ type: 'out', text: 'session reset' });
    setUsername('');
    setPassword('');
    setInput('');
    setStep('username');
    setLoading(false);
  };

  const runLogin = async (user, pass, totp = null) => {
    setLoading(true);
    appendHistory({ type: 'out', text: 'authenticating…' });
    try {
      const result = await login(user, pass, totp);
      if (result && result.requiresTwoFactor) {
        appendHistory({ type: 'out', text: '2fa required — enter totp code' });
        setStep('2fa');
        setInput('');
        setLoading(false);
        return;
      }
      appendHistory({ type: 'out', text: 'access granted. loading panel…' });
      setTimeout(() => navigate('/'), 400);
    } catch (err) {
      const apiErr = err.response?.data?.error;
      const apiMsg = err.response?.data?.message;
      const combined = [apiErr, apiMsg].filter(Boolean).join(' — ');
      let msg;
      if (!err.response) {
        msg = err.message === 'Network Error'
          ? 'error: cannot reach api — check connection'
          : combined || err.message;
      } else if (err.response.status === 403) {
        msg = combined || 'access denied — your ip is not whitelisted';
      } else if (err.response.status >= 500) {
        msg = combined || 'server error — try again or use desktop network';
      } else {
        msg = combined || 'login failed';
      }
      appendHistory({ type: 'err', text: msg });
      setUsername('');
      setPassword('');
      setStep('username');
      setInput('');
      setLoading(false);
    }
  };

  const cmdPrefix = () => {
    if (step === 'username') return 'login -u ';
    if (step === 'password') return 'login -p ';
    if (step === '2fa') return 'totp ';
    return '';
  };

  const displayInput = () => {
    if (step === 'password') return '•'.repeat(input.length);
    return input;
  };

  const handleKeyDown = async (e) => {
    if (loading) return;

    if (e.key === 'c' && (e.metaKey || e.ctrlKey) && step === '2fa') {
      e.preventDefault();
      resetSession();
      return;
    }

    if (e.key !== 'Enter') return;
    e.preventDefault();

    const value = step === 'password' ? input : input.trim();

    if (step === 'username') {
      if (!value) return;
      appendHistory({ type: 'cmd', text: `login -u ${value}` });
      setUsername(value);
      setInput('');
      setStep('password');
      return;
    }

    if (step === 'password') {
      if (!value) return;
      appendHistory({ type: 'cmd', text: 'login -p ********' });
      setPassword(value);
      setInput('');
      await runLogin(username, value);
      return;
    }

    if (step === '2fa') {
      if (value.length !== 6) {
        appendHistory({ type: 'err', text: 'totp must be 6 digits' });
        setInput('');
        return;
      }
      appendHistory({ type: 'cmd', text: `totp ${value}` });
      setInput('');
      await runLogin(username, password, value);
    }
  };

  const handleChange = (e) => {
    let v = e.target.value;
    if (step === '2fa') v = v.replace(/\D/g, '').slice(0, 6);
    setInput(v);
  };

  return (
    <div
      className="login-scene login-terminal-only"
      onClick={focusInput}
      role="presentation"
    >
      <div className="login-grid" aria-hidden />
      <div className="login-scanline" aria-hidden />

      <div className="login-brand">
        <CkbLogo size={64} showText textClassName="text-xl font-bold tracking-widest text-slate-100" />
      </div>

      <div className="login-terminal-window">
        <div className="login-terminal-chrome">
          <div className="login-terminal-dots">
            <span />
            <span />
            <span />
          </div>
          <span className="login-terminal-title-bar">CKB — auth</span>
        </div>

        <div className="login-terminal-body">
          {history.map((line, idx) => (
            <HistoryLine key={idx} line={line} />
          ))}

          {!loading && step !== 'boot' && (
            <div className="login-terminal-line login-active-line">
              <span className="prompt">{PROMPT}</span>
              <span className="cmd">
                {cmdPrefix()}
                {displayInput()}
              </span>
              <span className="login-shell-cursor" />
            </div>
          )}

          {loading && (
            <div className="login-terminal-line">
              <span className="out login-blink">▌ authenticating</span>
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          type={step === 'password' ? 'password' : step === '2fa' ? 'tel' : 'text'}
          inputMode={step === '2fa' ? 'numeric' : 'text'}
          value={input}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className="login-terminal-input"
          disabled={step === 'boot' || loading}
          autoComplete={
            step === 'username' ? 'username' : step === 'password' ? 'current-password' : 'one-time-code'
          }
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint={step === '2fa' ? 'done' : 'next'}
          aria-label={
            step === 'username' ? 'Username' : step === 'password' ? 'Password' : 'Two-factor code'
          }
        />
      </div>

      <p className="login-terminal-hint font-mono">
        tap terminal to type · enter ↵ · ctrl+c reset (2fa)
      </p>
    </div>
  );
}
